import type { Relations, SuretyAccountId } from "@inrev/common";
import { useFetchAssistantEnabled } from "@portal/api/assistant/admin";
import { useAccountComments } from "@portal/api/comments";
import {
	MessageComposer,
	messageDraftKey,
	useHasMessageDraft,
} from "@portal/components/features/message-draft";
import { DockedPanel } from "@portal/components/layout/DockedPanel";
import { IconTile, SegmentedControls } from "@portal/components/primitives";
import { CommentsTab, CommentsTabContent } from "@portal/components/ui/CommentsTab";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { FC, useMemo, useState } from "react";
import { AdminAssistantTool } from "../../admin/shared/tools/assistant/AdminAssistantTool";
import { VerniHeaderActions } from "./VerniHeaderActions";
import { VerniHistoryList } from "./VerniHistoryList";
import { useVerniOverlay, useVerniOverlayState, VerniOverlayProvider } from "./VerniOverlay";

type AccountMessagesProps = {
	accountId: SuretyAccountId;
	accountName: string;
};

const useDraftLabel = (accountId: SuretyAccountId, label: string, draftLabel = "Draft") =>
	useHasMessageDraft(messageDraftKey("accounts", accountId)) ? `${label} · ${draftLabel}` : label;

const AccountComments = ({ accountId }: Pick<AccountMessagesProps, "accountId">) => {
	const { user } = useAuthenticatedUser();
	const { comments, commentsLoading, createComment, createCommentLoading } = useAccountComments(
		accountId,
		user.type === "admin",
	);
	const label = useDraftLabel(accountId, "Account Messages");

	return (
		<CommentsTab
			comments={comments}
			commentsLoading={commentsLoading}
			composer={
				<MessageComposer
					draftKey={messageDraftKey("accounts", accountId)}
					createComment={createComment}
					sending={createCommentLoading}
				/>
			}
			contained
			label={label}
			trackingId="account-messages-toggle"
		/>
	);
};

type AdminAccountTool = "assistant" | "messages";

type AccountVerniProps = {
	accountId: SuretyAccountId;
	accountName: string;
	relation: Relations.Notes;
};

/** Verni's conversation, or the history list in its place. The chat stays mounted to keep its place. */
const AccountVerni = ({ accountId, accountName, relation }: AccountVerniProps) => {
	const { conversationId, openCount, openConversation, setConversationId, setCanStartNew, view } =
		useVerniOverlay();
	return (
		<>
			<div
				className={view === "chat" ? "flex min-h-0 flex-1 flex-col" : "hidden"}
				data-testid="verni-chat-view"
			>
				<AdminAssistantTool
					conversationId={conversationId}
					onCanStartNewChange={setCanStartNew}
					onConversationChange={setConversationId}
					openCount={openCount}
					relation={relation}
				/>
			</div>
			{view === "history" && (
				<VerniHistoryList
					accountId={accountId}
					accountName={accountName}
					autoFocus
					onNewConversation={() => openConversation("new")}
					onSelect={openConversation}
					selectedConversationId={conversationId}
					variant="compact"
				/>
			)}
		</>
	);
};

/**
 * Underwriters get both the assistant and the agent<->underwriter account thread from every tab,
 * docked behind a single toggle -- swapping the assistant panel in for the comments tab (rather
 * than adding it alongside) would leave the thread reachable only from the Overview tab.
 */
const AdminAccountMessages = ({ accountId, accountName }: AccountMessagesProps) => {
	const [tool, setTool] = useState<AdminAccountTool>("assistant");
	const [panelOpen, setPanelOpen] = useState(false);
	const relation: Relations.Notes = useMemo(
		() => ({ relationType: "account", relationId: accountId }),
		[accountId],
	);
	const { assistantEnabled, assistantEnabledIsLoading } = useFetchAssistantEnabled();
	const showAssistant = assistantEnabled && !assistantEnabledIsLoading;
	const activeTool: AdminAccountTool = showAssistant ? tool : "messages";
	const verniOverlay = useVerniOverlayState(relation);

	const { comments, createComment, createCommentLoading } = useAccountComments(accountId, true);
	const label = useDraftLabel(
		accountId,
		activeTool === "assistant" ? "Verni" : "Account Messages",
		activeTool === "assistant" ? "Message draft" : "Draft",
	);
	const composer = (
		<MessageComposer
			draftKey={messageDraftKey("accounts", accountId)}
			createComment={createComment}
			sending={createCommentLoading}
		/>
	);

	return (
		<VerniOverlayProvider value={verniOverlay}>
			<DockedPanel
				actions={activeTool === "assistant" ? <VerniHeaderActions /> : undefined}
				contained
				deferChildrenUntilOpen
				label={label}
				leadingIcon={
					<IconTile
						filled
						size="sm"
						tone="primary"
						type={activeTool === "assistant" ? "sparkles" : "chat-bubble-oval-left"}
					/>
				}
				onOpenChange={setPanelOpen}
				testId="account-chat-panel"
				trackingId="account-chat-toggle"
			>
				{showAssistant && (
					<div className="flex shrink-0 justify-center border-b border-b-bc-subtle px-3 py-2">
						<SegmentedControls<AdminAccountTool>
							onChange={setTool}
							trackingId="account-chat-tool-toggle"
							value={activeTool}
						>
							<SegmentedControls.Segment value="assistant">Verni</SegmentedControls.Segment>
							<SegmentedControls.Segment value="messages">Messages</SegmentedControls.Segment>
						</SegmentedControls>
					</div>
				)}
				{showAssistant ? (
					<>
						<div
							className={activeTool === "assistant" ? "flex min-h-0 flex-1 flex-col" : "hidden"}
							data-testid="account-assistant-content"
						>
							<AccountVerni accountId={accountId} accountName={accountName} relation={relation} />
						</div>
						<div
							className={activeTool === "messages" ? "flex min-h-0 flex-1 flex-col" : "hidden"}
							data-testid="account-messages-content"
						>
							<CommentsTabContent
								comments={comments}
								commentsOpen={panelOpen && activeTool === "messages"}
								composer={composer}
							/>
						</div>
					</>
				) : (
					<CommentsTabContent comments={comments} commentsOpen={panelOpen} composer={composer} />
				)}
			</DockedPanel>
		</VerniOverlayProvider>
	);
};

export const AccountMessages: FC<AccountMessagesProps> = ({ accountId, accountName }) => {
	const { user } = useAuthenticatedUser();
	if (user.type !== "admin") {
		return <AccountComments accountId={accountId} />;
	}

	return <AdminAccountMessages accountId={accountId} accountName={accountName} />;
};
